// Palette sampled from the Arjun & Siya reference card.
export const ROSES = {
  crimson: ["#5e0f1e", "#a01030", "#d24a62"],
  wine: ["#420a12", "#73131f", "#a8303c"],
  blush: ["#b44c66", "#e2869b", "#f8ccd4"],
  peach: ["#c26f37", "#f0b277", "#fde2bc"],
};

const BRACTS = [
  ["#8e0f2c", "#c02048", "#e2557c"],
  ["#a01030", "#d02050", "#ee7096"],
  ["#7c0c26", "#b01030", "#d84068"],
  ["#b01848", "#e04a7a", "#f69ab4"],
];

const radial = (id, [dark, mid, light], { cx = "50%", cy = "100%", r = "100%" } = {}) => (
  <radialGradient key={id} id={id} cx={cx} cy={cy} r={r} fx={cx} fy={cy}>
    <stop offset="0%" stopColor={dark} />
    <stop offset="55%" stopColor={mid} />
    <stop offset="100%" stopColor={light} />
  </radialGradient>
);

const linear = (id, stops, { x2 = "0", y2 = "1" } = {}) => (
  <linearGradient key={id} id={id} x1="0" y1="0" x2={x2} y2={y2}>
    {stops.map(([offset, color, opacity = 1]) => (
      <stop key={offset} offset={offset} stopColor={color} stopOpacity={opacity} />
    ))}
  </linearGradient>
);

/**
 * Shared gradients, filters and patterns for every Royal Mahal illustration.
 * Rendered once per page; the other inline SVGs reference these ids.
 */
const Defs = () => (
  <svg className="rm-defs" aria-hidden="true" focusable="false" width="0" height="0">
    <defs>
      {linear("rm-gold", [["0", "#ecd49a"], ["0.45", "#b78b4b"], ["1", "#87602c"]])}
      {linear("rm-gold-h", [["0", "#8c6430"], ["0.5", "#e6c88a"], ["1", "#8c6430"]], { x2: "1", y2: "0" })}
      {radial("rm-brass", ["#6e4b1c", "#c59a52", "#f6e0a6"], { cx: "35%", cy: "30%", r: "80%" })}

      {linear("rm-stone", [["0", "#e2c697"], ["0.6", "#c79b66"], ["1", "#a97a45"]])}
      {linear("rm-stone-shade", [["0", "#9c6c3c", 0.0], ["1", "#6c3f17", 0.55]], { x2: "1", y2: "0" })}
      {linear("rm-dome", [["0", "#ecd3a6"], ["0.5", "#d0a873"], ["1", "#a4743f"]], { x2: "1", y2: "0.2" })}
      {linear("rm-opening", [["0", "#5a3416"], ["1", "#7d5129"]])}

      {linear("rm-teak", [["0", "#7f4219"], ["0.5", "#6a3312"], ["1", "#4a220a"]], { x2: "1", y2: "0" })}
      {linear("rm-teak-panel", [["0", "#8d4f22"], ["1", "#5c2b0e"]])}

      {linear("rm-leaf", [["0", "#7a8a3c"], ["1", "#3c4814"]])}
      {linear("rm-leaf-dark", [["0", "#55621f"], ["1", "#2c360c"]])}

      {BRACTS.map((stops, i) => radial(`rm-bract-${i}`, stops))}

      {/* White pearls for the portrait frames */}
      <radialGradient id="rm-pearl" cx="38%" cy="34%" r="70%">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="45%" stopColor="#f8f3ea" />
        <stop offset="80%" stopColor="#e3d8c8" />
        <stop offset="100%" stopColor="#c9b9a3" />
      </radialGradient>

      {Object.entries(ROSES).map(([name, stops]) => radial(`rm-rose-${name}`, stops))}

      {/* Hand-painted feel: wobbly watercolour edges plus a light paper grain. */}
      <filter id="rm-watercolor" x="-4%" y="-4%" width="108%" height="108%">
        <feTurbulence type="fractalNoise" baseFrequency="0.04" numOctaves="2" seed="7" result="warp" />
        <feDisplacementMap in="SourceGraphic" in2="warp" scale="4" xChannelSelector="R" yChannelSelector="G" result="wobble" />
        <feGaussianBlur in="wobble" stdDeviation="0.35" result="soft" />
        <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" seed="11" result="grain" />
        <feColorMatrix in="grain" type="saturate" values="0" result="grainGrey" />
        <feComponentTransfer in="grainGrey" result="grainLight">
          <feFuncR type="linear" slope="0.3" intercept="0.78" />
          <feFuncG type="linear" slope="0.3" intercept="0.78" />
          <feFuncB type="linear" slope="0.3" intercept="0.78" />
        </feComponentTransfer>
        <feComposite in="grainLight" in2="soft" operator="in" result="grainIn" />
        <feBlend in="soft" in2="grainIn" mode="multiply" />
      </filter>
      <filter id="rm-paint-soft" x="-2%" y="-2%" width="104%" height="104%">
        <feTurbulence type="fractalNoise" baseFrequency="0.05" numOctaves="2" seed="4" result="warp" />
        <feDisplacementMap in="SourceGraphic" in2="warp" scale="1.6" xChannelSelector="R" yChannelSelector="G" result="wobble" />
        <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" seed="11" result="grain" />
        <feColorMatrix in="grain" type="saturate" values="0" result="grainGrey" />
        <feComponentTransfer in="grainGrey" result="grainLight">
          <feFuncR type="linear" slope="0.24" intercept="0.82" />
          <feFuncG type="linear" slope="0.24" intercept="0.82" />
          <feFuncB type="linear" slope="0.24" intercept="0.82" />
        </feComponentTransfer>
        <feComposite in="grainLight" in2="wobble" operator="in" result="grainIn" />
        <feBlend in="wobble" in2="grainIn" mode="multiply" />
      </filter>

      <pattern id="rm-jaali" width="18" height="18" patternUnits="userSpaceOnUse">
        <path
          d="M9 2.5 C10.6 5.6 12.4 7.4 15.5 9 C12.4 10.6 10.6 12.4 9 15.5 C7.4 12.4 5.6 10.6 2.5 9 C5.6 7.4 7.4 5.6 9 2.5 Z"
          fill="#6c3f17"
          fillOpacity="0.55"
        />
        <circle cx="0" cy="0" r="1.6" fill="#6c3f17" fillOpacity="0.45" />
        <circle cx="18" cy="0" r="1.6" fill="#6c3f17" fillOpacity="0.45" />
        <circle cx="0" cy="18" r="1.6" fill="#6c3f17" fillOpacity="0.45" />
        <circle cx="18" cy="18" r="1.6" fill="#6c3f17" fillOpacity="0.45" />
      </pattern>
    </defs>
  </svg>
);

export default Defs;
